import { useAuth0 } from '@auth0/auth0-react'
import {
  Alert,
  Box,
  IconButton
} from '@mui/material'
import {
  // IconBriefcase,
  IconInfoSquareRounded,
  IconStar,
  IconToolsKitchen2,
  IconX,
} from '@tabler/icons-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import NoResultsFound from '../../../components/EmptyScreen'
import ResactlyTab from '../../../components/ResactlyTab'
import { generateEater } from '../../../helpers/converters'
import { getLocation } from '../../../helpers/location-helper'
import { ROUTES } from '../../../helpers/routes-helper'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../store'
import { selectLocation } from '../../../store/authSlice'
import { selectEater } from '../../../store/profileSlice'
import {
  fetchPublicRestaurantByAlias,
  fetchRestaurantMatchDetails,
  getPredictedRestaurants,
  selectMessage,
  selectRestaurantDetail,
  selectRestaurantDishList,
  selectSeverity,
  selectShouldOpen,
  setOpen,
  setPredictedRestaurantDetail,
  setPublicRestaurantDetail,
  setRestaurantDetailFromResponse,
} from '../../../store/restaurantSlice'
import {
  getGroupMemberRestaurantDetail,
  selectGroup,
} from '../../../store/socialSlice'
import { PredictedRestaurantRequest } from '../../../types'
import useRudderStackAnalytics from '../../../useRudderAnalytics'
import { logEvent } from '../../../analytics/analytics'
import { persistAnonView } from '../../../services/rewardsService'
import { RestaurantViewedParams } from '../../../analytics/events'
import { getRecommendationAttribution } from '../../../helpers/recommendationAttribution'
import { ensureAnonId } from '../../../helpers/anon-id'
import { MuiAlert, MuiSnackbar } from '../styled/global.styled'
import AboutTab from './components/AboutTab'
import DishesTab from './components/DishesTab'
import RestaurantImages from './components/RestaurantImages'
import RestaurantInfo from './components/RestaurantInfo'
import ReviewsTab from './components/ReviewsTab'
import ReviewPromptBanner from '../../../components/restaurant/ReviewPromptBanner'
import MenuScanBanner from '../../../components/MenuScanBanner'
import { useMenuScanFlow } from '../../../hooks/useMenuScanFlow'
import {
  RestaurantDetailMuiContainer,
  RestaurantDetailRestaurantContainer,
  RestaurantDetailRestaurantIconChevronLeft,
  RestaurantDetailRestaurantTypography,
} from './styled/RestaurantDetail.styled'
import RestaurantDetailSkeleton from './components/RestaurantDetailSkeleton'
// import PartnershipTab from './components/PartnershipTab'

type RestaurantViewedSource = NonNullable<RestaurantViewedParams['source']>

const RestaurantDetail: React.FC = () => {
  const { navigate, location: routeLocation, dispatch } =
    useAppUtilities()
  const { alias } = useParams()
  const { startScan, uploading } = useMenuScanFlow()
  const { isAuthenticated } = useAuth0()
  const [analytics, isAnalyticsReady] = useRudderStackAnalytics()
  const queryParams = new URLSearchParams(routeLocation.search)
  // Exact rank attribution: the recommendation carousel passes recommendation_id + rank_position
  // via nav state when a rec card is clicked. Absent for direct-navigation / deep-link users
  // (the fields are then simply omitted from the emits below — no rank pollution).
  const navState = (routeLocation.state ?? {}) as {
    recommendation_id?: string
    rank_position?: number
    list_type?: string
    model_version?: string
    source?: RestaurantViewedSource
  }
  // Attribution resolution: nav state is the fast path; the sessionStorage store (keyed by alias,
  // written on the reco-card click) is the durable fallback for views that lost nav state — a hard
  // reload, a shared/deep-link re-entry, or a back/forward restore. This is why recommendation_id
  // was ~0% populated on the detail funnel before.
  const storedAttribution = useMemo(
    () => getRecommendationAttribution(alias),
    [alias],
  )
  const recommendationId =
    navState.recommendation_id ?? storedAttribution?.recommendationId
  const rankPosition = navState.rank_position ?? storedAttribution?.rankPosition
  // Model version that produced this recommendation (nav state fast path, sessionStorage durable
  // fallback) — passed to RestaurantInfo so the detail-page save / reserve accepts stamp it.
  const modelVersion =
    navState.model_version ?? storedAttribution?.modelVersion
  const name = queryParams.get('name')
  const groupId = queryParams.get('groupId')
  // Prefer memberId (opaque eater id, no PII). memberEmail is a legacy fallback for old links.
  const memberId = queryParams.get('memberId')
  const memberEmail = queryParams.get('memberEmail')
  const eater = useAppSelector(selectEater)
  const message = useAppSelector(selectMessage)
  const severity = useAppSelector(selectSeverity)
  const location = useAppSelector(selectLocation)
  const openServer = useAppSelector(selectShouldOpen)
  const restaurant = useAppSelector(selectRestaurantDetail)
  const restaurantDishList = useAppSelector(selectRestaurantDishList)
  const group = useAppSelector(selectGroup)
  // eslint-disable-next-line
  const handleMenuScanFile = useCallback(
    async (
      file: File,
      uploadMethod?: 'camera' | 'file_picker',
    ): Promise<void> => {
      try {
        await startScan(
          file,
          restaurant?.restaurantDTO?.restaurantId ?? alias ?? undefined,
          uploadMethod,
        )
      } catch {
        // no-op
      }
    },
    [startScan, restaurant?.restaurantDTO?.alias, alias],
  )
  const [changeTab, setChangeTab] = useState(0)
  const [pageTitle, setPageTitle] = useState('')
  const [staticLoading, setStaticLoading] = useState(false)
  
  const [groupMemberName, setGroupMemberName] = useState<string | null>(null)
  // eslint-disable-next-line
  const [showDishMatchText, setShowDishMatchText] = useState(true)

  useEffect(() => {
    if (restaurant) {
      const restaurantTitle = restaurant?.restaurantDTO?.name
        ? `Resactly | ${restaurant?.restaurantDTO.name} - Details`
        : 'Resactly | Restaurant Details'
      setPageTitle(restaurantTitle)
    }
  }, [restaurant])

  useEffect(() => {
    const restaurantTitle = restaurant?.restaurantDTO?.name
      ? `Resactly | ${restaurant?.restaurantDTO.name} - Details`
      : 'Resactly | Restaurant Details'

    const { fullName, eaterId, email } = generateEater(eater ?? {})

    if (analytics && isAnalyticsReady) {
      analytics?.page('RESTAURANT_PAGE_OPENED', {
        path: `${ROUTES.RESTAURANT_DETAILS}/${alias ?? ''}/details`,
        title: restaurantTitle,
        url: `${window.location.origin}${ROUTES.RESTAURANT_DETAILS}/${
          alias ?? ''
        }/details`,
        category: 'Restaurant',
        name: `${
          restaurant?.restaurantDTO?.name ?? ''
        } Restaurant Detail Viewed`,
        eaterName: fullName,
        email,
        eaterId,
        restaurantAlias: `${alias ?? ''}`,
        startTime: new Date().toISOString(),
      })
    }
  }, [analytics, isAnalyticsReady])

  useEffect(() => {
    const restaurantTitle = restaurant?.restaurantDTO?.name
      ? `Resactly | ${restaurant?.restaurantDTO.name} - Details`
      : 'Resactly | Restaurant Details'

    const { fullName, eaterId, email } = generateEater(eater ?? {})

    return () => {
      analytics?.page('RESTAURANT_PAGE_CLOSED', {
        path: `${ROUTES.RESTAURANT_DETAILS}/${
          restaurant?.restaurantDTO?.alias ?? ''
        }/details`,
        title: restaurantTitle,
        url: `${window.location.origin}${ROUTES.RESTAURANT_DETAILS}/${
          restaurant?.restaurantDTO?.alias ?? ''
        }/details`,
        category: 'Restaurant',
        name: `${
          restaurant?.restaurantDTO?.name ?? ''
        } Restaurant Detail Viewed`,
        eaterName: fullName,
        email,
        eaterId,
        restaurantAlias: `${restaurant?.restaurantDTO?.alias ?? ''}`,
        endTime: new Date().toISOString(),
      })
    }
  }, [])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setStaticLoading(true)
      if (groupId && (memberId ?? memberEmail)) {
        await groupMemberRestaurant()
      } else if (isAuthenticated) {
        await authenticatedRestaurant()
      } else {
        await unAuthenticatedRestaurant()
      }
      setStaticLoading(false)
    }

    void fetchData()

    if (name === 'review') {
      setChangeTab(1)
    }
  }, [alias, isAuthenticated, dispatch, groupId, memberId, memberEmail])

  // Open the Reviews tab whenever the ?name=review query changes — including when the
  // "Rate it" banner is tapped while already on this page (the fetch effect above doesn't
  // re-run on a query-only change, so the tab switch must react to `name` on its own).
  useEffect(() => {
    if (name === 'review') {
      setChangeTab(1)
    }
  }, [name])

  // Derive the arrival source instead of hardcoding 'home' (which mislabelled every search /
  // deep-link / saved / group arrival). Order: explicit source threaded by the navigator → group
  // context → an attributable recommendation (carousel / View All) → a cold deep-link (no nav state)
  // → any other in-app navigation.
  const viewedSource: RestaurantViewedSource = navState.source
    ? navState.source
    : groupId
      ? 'in_app'
      : recommendationId
        ? 'home'
        : routeLocation.state == null
          ? 'deep_link'
          : 'in_app'

  // Track restaurant_viewed when restaurant data is loaded
  useEffect(() => {
    if (restaurant?.restaurantDTO && !staticLoading) {
      logEvent('restaurant_viewed', {
        restaurant_id: restaurant.restaurantDTO.restaurantId ?? '',
        restaurant_name: restaurant.restaurantDTO.name,
        source: viewedSource,
        screen_name: 'restaurant_detail',
        recommendation_id: recommendationId,
        rank_position: rankPosition,
      })
    }
  }, [restaurant?.restaurantDTO, staticLoading, viewedSource, recommendationId, rankPosition])

  // Anon-only: durably persist a restaurant view so the provisional points number grows and the
  // view credits on signup claim. Guard against spamming — fire at most once per restaurant per
  // session (sessionStorage, with a ref fallback if storage throws). The backend also dedups per
  // (device, restaurant) and applies a daily cap, but the client guard keeps the number honest and
  // avoids redundant requests as the view effect's deps mutate.
  const persistedAnonViewsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (isAuthenticated || staticLoading) return
    const restaurantId = restaurant?.restaurantDTO?.restaurantId
    const restaurantAlias = restaurant?.restaurantDTO?.alias
    const viewKey = restaurantId ?? restaurantAlias
    if (!viewKey) return
    const storageKey = `rsly_anon_view_${viewKey}`
    if (persistedAnonViewsRef.current.has(viewKey)) return
    try {
      if (sessionStorage.getItem(storageKey) === '1') {
        persistedAnonViewsRef.current.add(viewKey)
        return
      }
      sessionStorage.setItem(storageKey, '1')
    } catch {
      // sessionStorage unavailable — fall back to the per-mount ref guard.
    }
    persistedAnonViewsRef.current.add(viewKey)
    void persistAnonView(restaurantId, restaurantAlias)
  }, [
    restaurant?.restaurantDTO?.restaurantId,
    restaurant?.restaurantDTO?.alias,
    isAuthenticated,
    staticLoading,
  ])

  // Latest loaded restaurant_id, held in a ref so the unmount dwell emit (which captures the mount
  // closure) reads the restaurant that was actually deliberated on rather than the empty mount value.
  const restaurantIdRef = useRef<string>('')
  useEffect(() => {
    restaurantIdRef.current = restaurant?.restaurantDTO?.restaurantId ?? ''
  }, [restaurant?.restaurantDTO?.restaurantId])

  // First-party dwell/close: time-on-detail + restaurant_id + recommendation_id, emitted once on
  // unmount. Only fires when a restaurant actually loaded (a bounce before load is not a deliberation).
  const detailOpenedAtRef = useRef<number>(Date.now())
  useEffect(() => {
    detailOpenedAtRef.current = Date.now()
    return () => {
      const restaurantId = restaurantIdRef.current
      if (!restaurantId) return
      logEvent('restaurant_dwell', {
        restaurant_id: restaurantId,
        dwell_ms: Date.now() - detailOpenedAtRef.current,
        recommendation_id: recommendationId,
        screen_name: 'restaurant_detail',
      })
    }
  }, [])

  // Scroll-depth milestones (25/50/75/100%) — deliberation depth on the core detail screen. Each
  // milestone fires at most once per mounted restaurant. Passive window listener, cleaned up on
  // unmount; a no-op (no signal) if the surface scrolls in an inner container rather than the window.
  useEffect(() => {
    const restaurantId = restaurant?.restaurantDTO?.restaurantId
    if (!restaurantId || staticLoading) return
    const milestones = [25, 50, 75, 100]
    const fired = new Set<number>()
    const onScroll = (): void => {
      const el = document.documentElement
      const scrollable = el.scrollHeight - el.clientHeight
      if (scrollable <= 0) return
      const pct = Math.min(100, Math.round((el.scrollTop / scrollable) * 100))
      for (const m of milestones) {
        if (pct >= m && !fired.has(m)) {
          fired.add(m)
          logEvent('scroll_depth', {
            restaurant_id: restaurantId,
            depth_percent: m,
            screen_name: 'restaurant_detail',
          })
        }
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [restaurant?.restaurantDTO?.restaurantId, staticLoading])

  // Tab deliberation signals: tab_selected on every switch, plus a section-specific view event
  // (menu_opened when the Dishes/menu tab is opened, reviews_viewed for the Reviews tab). Fires only
  // on a real user tab change (ResactlyTab.onChangeTab), never on the default mount.
  const handleTabChange = useCallback(
    (item: { value?: string }): void => {
      const restaurantId = restaurant?.restaurantDTO?.restaurantId ?? ''
      logEvent('tab_selected', {
        tab_name: item?.value ?? '',
        screen_name: 'restaurant_detail',
        source: 'in_app',
      })
      if (item?.value === 'dishes') {
        logEvent('menu_opened', {
          restaurant_id: restaurantId,
          recommendation_id: recommendationId,
          screen_name: 'restaurant_detail',
        })
      } else if (item?.value === 'reviews') {
        logEvent('reviews_viewed', {
          restaurant_id: restaurantId,
          recommendation_id: recommendationId,
          screen_name: 'restaurant_detail',
        })
      }
    },
    [restaurant?.restaurantDTO?.restaurantId, recommendationId],
  )

  // Fetch match-details — authenticated users via the JWT route, anon users via the public variant
  // (resolved by anonId). The card keeps its "still learning" empty state when matchPercentage is
  // null (no engagement signal to synthesize from).
  useEffect(() => {
    if (!alias || !restaurant) return
    if (isAuthenticated) {
      void dispatch(fetchRestaurantMatchDetails({ alias }))
    } else {
      void ensureAnonId().then((anonId) => {
        void dispatch(fetchRestaurantMatchDetails({ alias, anonId }))
      })
    }
  }, [alias, restaurant, dispatch, isAuthenticated])

  const setOpenServer = (): void => {
    dispatch(setOpen(false))
  }

  const unAuthenticatedRestaurant = async (): Promise<void> => {
    const result = await dispatch(fetchPublicRestaurantByAlias(alias ?? ''))
    if (fetchPublicRestaurantByAlias.fulfilled.match(result)) {
      dispatch(setPublicRestaurantDetail())
    }
  }

  const groupMemberRestaurant = async (): Promise<void> => {
    if (!groupId || (!memberId && !memberEmail) || !alias) return

    const result = await dispatch(
      getGroupMemberRestaurantDetail({
        groupId,
        alias,
        memberId: memberId ?? undefined,
        memberEmail: memberEmail ?? undefined,
      }),
    )

    if (getGroupMemberRestaurantDetail.fulfilled.match(result)) {
      const payload = result.payload
      if (payload?.result && payload.result.length > 0) {
        const predictedRestaurant = payload.result[0]
        // Set the restaurant detail directly from the response
        dispatch(setRestaurantDetailFromResponse(predictedRestaurant))
        // Find member name from group members if available
        if (group?.members) {
          const member = group.members.find((m) =>
            memberId ? m.eaterId === memberId : m.email === memberEmail,
          )
          if (member) {
            setGroupMemberName(member.name)
          } else if (predictedRestaurant.recommendation?.name) {
            setGroupMemberName(predictedRestaurant.recommendation.name)
          }
        } else if (predictedRestaurant.recommendation?.name) {
          setGroupMemberName(predictedRestaurant.recommendation.name)
        }
      } else {
        // Fallback to regular flow if group member endpoint returns no results
        await authenticatedRestaurant()
      }
    }
  }

  const authenticatedRestaurant = async (): Promise<void> => {
    let local = location
    if (location === undefined) {
      local = await getLocation()
    }
    const request: PredictedRestaurantRequest = {
      aliases: [alias ?? ''],
      includeMenus: true,
      longitude: local?.longitude,
      latitude: local?.latitude,
    }
    const result = await dispatch(getPredictedRestaurants(request))
    if (getPredictedRestaurants.fulfilled.match(result)) {
      if (result.payload.count < 1) {
        await unAuthenticatedRestaurant()
      } else {
        dispatch(
          setPredictedRestaurantDetail({
            alias: alias ?? '',
          }),
        )
      }
    }
  }

  const tabComponentListDynamic = [
    <div key="dishes">
      <DishesTab
        showMatchText={showDishMatchText}
        onFileSelected={(file, uploadMethod) => { void handleMenuScanFile(file, uploadMethod) }}
        disabled={uploading}
      />
    </div>,
    <div key="reviews">
      <ReviewsTab />
    </div>,
    <div key="about">
      <AboutTab />
    </div>,
    // <div key="partnership">
    //   <PartnershipTab />
    // </div>,
  ]

  return (
    <RestaurantDetailMuiContainer>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={`Manage your Resactly profile for ${
            restaurant?.restaurantDTO?.name ?? ''
          }, view your dining preferences, saved restaurants, and personalized recommendations. Enhance your restaurant discovery journey with tailored insights.`}
        />
      </Helmet>
      {!staticLoading ? (
        restaurant ? (
          <div>
            <RestaurantDetailRestaurantContainer>
              <RestaurantDetailRestaurantIconChevronLeft />
              <RestaurantDetailRestaurantTypography
                onClick={() => {
                  if (groupId) {
                    navigate(`${ROUTES.SOCIAL}/${groupId}`)
                  } else {
                    navigate(-1)
                  }
                }}
              >
                {groupId ? 'Back to group' : 'Back'}
              </RestaurantDetailRestaurantTypography>
            </RestaurantDetailRestaurantContainer>
            <ReviewPromptBanner
              restaurantId={restaurant?.restaurantDTO?.restaurantId}
              alias={restaurant?.restaurantDTO?.alias ?? alias ?? ''}
            />
            {groupId && groupMemberName && (
              <Box sx={{ mb: 2 }}>
                <Alert
                  severity="info"
                  action={
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigate(
                          `${ROUTES.RESTAURANT_DETAILS}/${alias}/details`,
                        )
                      }}
                    >
                      <IconX size={16} />
                    </IconButton>
                  }
                >
                  Viewing recommendations for{' '}
                  <strong>{groupMemberName}</strong> in group{' '}
                  <strong>{group?.name ?? ''}</strong>
                </Alert>
              </Box>
            )}
            <RestaurantImages />
            <RestaurantInfo
              recommendationId={recommendationId}
              rankPosition={rankPosition}
              modelVersion={modelVersion}
            />
            <MenuScanBanner
              entryPoint="restaurant_detail"
              restaurantAlias={restaurant?.restaurantDTO?.alias ?? alias ?? undefined}
              onFileSelected={(file, uploadMethod) => { void handleMenuScanFile(file, uploadMethod) }}
              disabled={uploading}
              hasMenu={Boolean(restaurantDishList?.length)}
            />
            {/* All three tabs (Dishes, Reviews, About) are public: anonymous users can view and
                submit reviews (POST /reviews/public/{alias}/dining-review), so Reviews is no
                longer gated behind auth. */}
            <ResactlyTab
              list={tabList}
              tabComponent={tabComponentListDynamic}
              align="start"
              changeTab={changeTab}
              onChangeTab={handleTabChange}
            />
          </div>
        ) : (
          <NoResultsFound
            containerHeight="calc(100vh - 208px)"
            mainText="No data available"
            secondaryText="Requested information is not found"
          />
        )
      ) : (
        <RestaurantDetailSkeleton />
      )}

      <MuiSnackbar open={openServer} onClose={setOpenServer}>
        <MuiAlert onClose={setOpenServer} severity={severity ?? 'success'}>
          {message}
        </MuiAlert>
      </MuiSnackbar>
    </RestaurantDetailMuiContainer>
  )
}

export default RestaurantDetail

const tabList = [
  {
    label: 'Dishes',
    value: 'dishes',
    icon: <IconToolsKitchen2 stroke={2} />,
  },
  {
    label: 'Reviews',
    value: 'reviews',
    icon: <IconStar stroke={2} />,
  },
  {
    label: 'About',
    value: 'about',
    icon: <IconInfoSquareRounded stroke={2} />,
  },
  // {
  //   label: 'Partnership',
  //   value: 'partnership',
  //   icon: <IconBriefcase stroke={2} />,
  // },
]
